/**
 * View of a page: editable title + blocks editor with autosave,
 * icon, organization sharing, backlinks, and attachments.
 * Mounted keyed by pageId (route remounts component on navigation).
 */
import { createMemo, createSignal, Show } from "solid-js";
import {
  AlertCircle,
  Check,
  History,
  Loader2,
  MessageSquareText,
  PenLine,
  Share2,
  Users,
} from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import type { PageVersion } from "@folio/powersync";
import type { NotePageSearch } from "@/routes/_app/notes.$pageId";
import { cn, contentBlockRows } from "@folio/shared";
import { usePageBlocksQuery, usePageDetailQuery } from "@folio/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PullToDismiss } from "@/components/ui/pull-to-dismiss";
import { Button } from "@/components/ui/button";
import { IconPicker } from "@/features/notes/components/icon-picker";
import { PageEditor } from "./page-editor";
import { PageShareDialog } from "./page-share-dialog";
import { VersionHistorySheet } from "./version-history-sheet";
import { PageBacklinks } from "./page-backlinks";
import { PageAttachments } from "./page-attachments";
import { PageBreadcrumbs } from "./page-breadcrumbs";
import { DocumentActions } from "@/features/documents/components/document-actions";
import { CollaborateButton } from "@/features/documents/components/collaborate-button";
import { usePageComments } from "@/features/comments/hooks/use-page-comments";
import { CommentsPanel } from "@/features/comments/components/comments-panel";
import { selectionText } from "@/features/comments/lib/suggestion-ops";
import type { Editor } from "@tiptap/core";
import { usePageEditor } from "../hooks/use-page-editor";
import { useNoteCarousel } from "../hooks/use-note-carousel";
import { flattenPageTree, usePageTree } from "../hooks/use-page-tree";
import { AppErrorBoundary } from "@/components/error-boundary";

const TITLE_SAVE_DEBOUNCE_MS = 400;

interface NotePageViewProps {
  pageId: string;
  onRename: (id: string, title: string) => void;
  onSetIcon: (id: string, icon: string | null) => void;
  /** Restore a version from history (forward write). */
  onRestoreVersion: (id: string, version: PageVersion) => Promise<void>;
}

export function NotePageView(props: NotePageViewProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const navigate = useNavigate();

  const page = usePageDetailQuery({
    db: () => db(),
    drizzle,
    pageId: () => props.pageId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  // Resolves the page: present / loading (sync) / not-found / offline. Prevents
  // opening a deleted page, an invalid id, or a wiki-link to a non-existent
  // page from leaving editor loading forever with a blank "Untitled" title.
  // A page in the trash is treated as not-found in detail view
  // (managed from /trash; its wiki-links cease resolving).
  const resolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(page.data && page.data.deleted_at === null),
    isPending: () => page.isLoading,
  });

  const editorState = usePageEditor(() => props.pageId);

  // Captures editor instance for document actions (export/import
  // .docx), which need to read/replace live editor content.
  const [editorInstance, setEditorInstance] = createSignal<Editor | null>(null);
  const handleEditorReady = (instance: Editor) => {
    editorState.handleReady(instance);
    setEditorInstance(instance);
  };

  // URL-addressable overlays (`?share=1`, `?icon=1`): query is the
  // single source of truth, so a copied link reopens the exact same dialog.
  // Idempotent: `replace` avoids inflating history and open/close only toggles
  // relevant search-param preserving remainder of query.
  const urlSearch = useSearch({ from: "/_app/documents/$pageId" });
  const setShareOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...(prev as NotePageSearch), share: open ? true : undefined }),
      replace: true,
    });
  };
  const setIconOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...(prev as NotePageSearch), icon: open ? true : undefined }),
      replace: true,
    });
  };
  const shareOpen = () => urlSearch().share === true;
  const iconOpen = () => urlSearch().icon === true;
  const setVersionsOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...(prev as NotePageSearch), versions: open ? true : undefined }),
      replace: true,
    });
  };
  const versionsOpen = () => urlSearch().versions === true;

  // Current blocks (history diff reference). Shares queryKey
  // (and thus cache) with editor subscription.
  const currentBlocks = usePageBlocksQuery({
    db: () => db(),
    drizzle,
    pageId: () => props.pageId,
  });
  const [restoring, setRestoring] = createSignal(false);

  // ── Comentarios y sugerencias ──
  const comments = usePageComments({
    pageId: () => props.pageId,
    editor: editorInstance,
    blocks: () => currentBlocks.data ?? [],
  });
  const [suggestionOn, setSuggestionOn] = createSignal(false);
  const [pendingAnchor, setPendingAnchor] = createSignal<string | null>(null);
  const [focusedThreadId, setFocusedThreadId] = createSignal<string | null>(null);
  const setCommentsOpen = (open: boolean) => {
    if (!open) {
      setPendingAnchor(null);
      setFocusedThreadId(null);
    }
    void navigate({
      to: ".",
      search: (prev) => ({ ...(prev as NotePageSearch), comments: open ? true : undefined }),
      replace: true,
    });
  };
  const commentsOpen = () => urlSearch().comments === true;

  const handleRestore = async (version: PageVersion) => {
    if (restoring()) {
      return;
    }
    setRestoring(true);
    try {
      await props.onRestoreVersion(props.pageId, version);
      setVersionsOpen(false);
    } finally {
      setRestoring(false);
    }
  };

  // ── Sibling note carousel (horizontal swipe) ──
  // Flat page tree list (sidebar visual order) defines "previous" and
  // "next"; swiping navigates to adjacent page. Route remounts detail.
  const { tree } = usePageTree();
  const flatPages = createMemo(() => flattenPageTree(tree()));
  const currentIndex = createMemo(() =>
    flatPages().findIndex((entry) => entry.id === props.pageId),
  );
  const neighborId = (offset: number): string | null => {
    const index = currentIndex();
    if (index < 0) {
      return null;
    }
    return flatPages()[index + offset]?.id ?? null;
  };

  let surfaceRef: HTMLDivElement | undefined;
  const carousel = useNoteCarousel({
    getPrevId: () => neighborId(-1),
    getNextId: () => neighborId(1),
    navigate: (pageId) => {
      void navigate({ to: "/documents/$pageId", params: { pageId } });
    },
    getSurfaceRef: () => surfaceRef,
  });

  // Title uses "last local edit wins" while typing; when
  // no edit is in flight it reflects synced value.
  const [draftTitle, setDraftTitle] = createSignal<string | null>(null);
  let titleTimeout: ReturnType<typeof setTimeout> | null = null;

  const title = createMemo(() => draftTitle() ?? page.data?.title ?? "");

  const handleTitleInput = (value: string) => {
    setDraftTitle(value);
    if (titleTimeout) {
      clearTimeout(titleTimeout);
    }
    titleTimeout = setTimeout(() => {
      titleTimeout = null;
      props.onRename(props.pageId, value);
      setDraftTitle(null);
    }, TITLE_SAVE_DEBOUNCE_MS);
  };

  return (
    <Show
      when={resolution() === "ready" || resolution() === "loading"}
      fallback={
        <div class="flex h-full min-w-0 flex-1 items-center justify-center">
          <NotFoundState
            variant={resolution() === "offline" ? "offline" : "not-found"}
            title={resolution() === "offline" ? undefined : t(msg`Note not found`)}
            description={
              resolution() === "offline"
                ? undefined
                : t(msg`This note may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Back to notes`),
              onClick: () => {
                void navigate({ to: "/documents" });
              },
            }}
          />
        </div>
      }
    >
      <div
        ref={(element) => {
          surfaceRef = element;
        }}
        class="flex h-full min-w-0 flex-1 flex-col overflow-y-auto"
        style={{ "touch-action": "pan-y" }}
        onPointerDown={carousel.handlePointerDown}
        onPointerMove={carousel.handlePointerMove}
        onPointerUp={carousel.handlePointerUp}
        onPointerCancel={carousel.handlePointerCancel}
      >
        <div
          class="mx-auto flex w-full max-w-3xl flex-1 flex-col px-6 py-8 lg:px-10"
          style={{
            transform:
              carousel.dragX() === 0 ? undefined : `translate3d(${carousel.dragX()}px, 0, 0)`,
            transition: carousel.isAnimating()
              ? `transform ${carousel.transitionMs}ms cubic-bezier(0.32, 0.72, 0, 1)`
              : undefined,
            "will-change": carousel.dragX() === 0 ? "auto" : "transform",
          }}
        >
          {/* Breadcrumbs del árbol: ruta de vuelta hacia arriba (interconexión). */}
          <PageBreadcrumbs pageId={props.pageId} />
          {/* flex-wrap: con la barra de acciones (historial/sugerencias/
              comentarios/exportar/colaborar/compartir) el título nunca se
              comprime a 0 — los botones bajan a otra línea antes que estrujarlo
              (sin scroll-x). */}
          <div class="flex flex-wrap items-center gap-2">
            <Popover open={iconOpen()} onOpenChange={setIconOpen}>
              <PopoverTrigger
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-2xl transition-transform motion-safe:will-change-transform hover:bg-accent/10 active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-label={t(msg`Page icon`)}
                title={t(msg`Page icon`)}
              >
                <Show when={page.data?.icon} fallback={<span class="opacity-40">📄</span>}>
                  {page.data?.icon}
                </Show>
              </PopoverTrigger>
              <PopoverContent class="w-72">
                {/* Cierre por arrastre vertical (toque): mismo estándar de gestos. */}
                <PullToDismiss onDismiss={() => setIconOpen(false)}>
                  <IconPicker
                    selectedIcon={page.data?.icon ?? ""}
                    onSelect={(icon) => {
                      props.onSetIcon(props.pageId, icon === page.data?.icon ? null : icon);
                      setIconOpen(false);
                    }}
                  />
                </PullToDismiss>
              </PopoverContent>
            </Popover>
            <input
              type="text"
              value={title()}
              onInput={(e) => {
                handleTitleInput(e.currentTarget.value);
              }}
              placeholder={t(msg`Untitled`)}
              class="min-w-[10rem] flex-1 bg-transparent text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/40"
              aria-label={t(msg`Page title`)}
            />
            {/* Feedback de autosave: guardando → error → guardado. */}
            <Show
              when={editorState.saveError()}
              fallback={
                <Show
                  when={editorState.isSaving()}
                  fallback={
                    <Show when={editorState.lastSavedAt()}>
                      <span
                        class="flex shrink-0 items-center gap-1 text-xs font-medium text-success motion-safe:animate-fade-in"
                        aria-live="polite"
                      >
                        <Check class="h-3.5 w-3.5" />
                        <span class="hidden sm:inline">{t(msg`Saved`)}</span>
                      </span>
                    </Show>
                  }
                >
                  <span
                    class="flex shrink-0 items-center gap-1 text-xs text-muted-foreground/60"
                    aria-live="polite"
                  >
                    <Loader2 class="h-3.5 w-3.5 animate-spin" />
                    <span class="hidden sm:inline">{t(msg`Saving…`)}</span>
                  </span>
                </Show>
              }
            >
              <span
                class="flex shrink-0 items-center gap-1 text-xs font-medium text-danger"
                aria-live="assertive"
                role="status"
              >
                <AlertCircle class="h-3.5 w-3.5" />
                <span class="hidden sm:inline">{t(msg`Save failed`)}</span>
              </span>
            </Show>
            <Button
              variant="ghost"
              size="sm"
              class={cn("shrink-0 gap-1.5", suggestionOn() && "bg-accent/15 text-accent")}
              data-testid="suggestion-mode-toggle"
              aria-pressed={suggestionOn()}
              onClick={() => setSuggestionOn((on) => !on)}
              aria-label={t(msg`Suggestion mode`)}
              title={t(msg`Suggestion mode`)}
            >
              <PenLine class="h-4 w-4" />
              <span class="hidden sm:inline">{t(msg`Suggest`)}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              class="relative shrink-0 gap-1.5"
              data-testid="comments-open"
              onClick={() => setCommentsOpen(true)}
              aria-label={t(msg`Comments`)}
              title={t(msg`Comments`)}
            >
              <MessageSquareText class="h-4 w-4" />
              <span class="hidden sm:inline">{t(msg`Comments`)}</span>
              <Show when={comments.openCount() > 0}>
                <span
                  class="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-accent-foreground"
                  data-testid="comments-count"
                >
                  {comments.openCount()}
                </span>
              </Show>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              class="shrink-0 gap-1.5"
              data-testid="version-history-open"
              onClick={() => setVersionsOpen(true)}
              aria-label={t(msg`Version history`)}
              title={t(msg`Version history`)}
            >
              <History class="h-4 w-4" />
              <span class="hidden sm:inline">{t(msg`History`)}</span>
            </Button>
            <DocumentActions editor={editorInstance} title={title} />
            <CollaborateButton editor={editorInstance} />
            <Button
              variant="ghost"
              size="sm"
              class="shrink-0 gap-1.5"
              onClick={() => setShareOpen(true)}
              aria-label={t(msg`Share page`)}
              title={t(msg`Share page`)}
            >
              <Show when={page.data?.is_shared === 1} fallback={<Share2 class="h-4 w-4" />}>
                <Users class="h-4 w-4 text-accent" />
              </Show>
              <span class="hidden sm:inline">{t(msg`Share`)}</span>
            </Button>
          </div>
          <div class="mt-4 flex flex-1 flex-col">
            {/* El editor TipTap es la región más propensa a romper en runtime
              (documentos corruptos, extensiones): su propio ErrorBoundary evita
              que un fallo del editor tire toda la página. */}
            <AppErrorBoundary
              variant="region"
              label="editor"
              title={t(msg`The editor could not be loaded`)}
              description={t(
                msg`We could not render this note's content. Retry, or reload the page.`,
              )}
            >
              <Show
                when={editorState.isReady()}
                fallback={
                  <div class="flex flex-1 items-center justify-center">
                    <Loader2 class="h-5 w-5 animate-spin text-muted-foreground/50" />
                  </div>
                }
              >
                <PageEditor
                  initialDoc={editorState.initialDoc()}
                  onDocUpdate={editorState.handleDocUpdate}
                  onReady={handleEditorReady}
                  comments={{
                    suggestionMode: suggestionOn,
                    onCommentClick: (threadId) => {
                      setFocusedThreadId(threadId);
                      setCommentsOpen(true);
                    },
                    onSuggestInsert: comments.onSuggestInsert,
                    onSuggestDelete: comments.onSuggestDelete,
                    onAddComment: () => {
                      const editor = editorInstance();
                      if (!editor) {
                        return;
                      }
                      const anchor = selectionText(editor).trim();
                      if (anchor === "") {
                        return;
                      }
                      setPendingAnchor(anchor);
                      setFocusedThreadId(null);
                      setCommentsOpen(true);
                    },
                  }}
                />
              </Show>
            </AppErrorBoundary>
          </div>
          <AppErrorBoundary variant="region" label="backlinks">
            <PageBacklinks pageId={props.pageId} />
          </AppErrorBoundary>
          <AppErrorBoundary variant="region" label="attachments">
            <PageAttachments
              pageId={props.pageId}
              expandedId={urlSearch().attachment ?? null}
              onExpandedChange={(id) => {
                void navigate({
                  to: ".",
                  search: (prev) => ({ ...(prev as NotePageSearch), attachment: id ?? undefined }),
                  replace: true,
                });
              }}
            />
          </AppErrorBoundary>
        </div>
        <PageShareDialog pageId={props.pageId} open={shareOpen()} onOpenChange={setShareOpen} />
        <CommentsPanel
          open={commentsOpen()}
          onOpenChange={setCommentsOpen}
          comments={comments}
          pendingAnchor={pendingAnchor}
          onPendingDone={() => setPendingAnchor(null)}
          focusedThreadId={focusedThreadId}
        />
        <VersionHistorySheet
          open={versionsOpen()}
          onOpenChange={setVersionsOpen}
          pageId={() => props.pageId}
          currentTitle={() => page.data?.title ?? ""}
          // CONTENT blocks only: sidecars (comment threads) do not
          // form part of the history diff.
          currentBlocks={() => contentBlockRows(currentBlocks.data ?? [])}
          onRestore={(version) => {
            void handleRestore(version);
          }}
          restoring={restoring}
        />
      </div>
    </Show>
  );
}
